import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { sspSubmitOrder, draftTeams as recoilStoreDraftTeams } from 'pages/provisioning/day2/github-onboarding-new/store';
import { getCustomTeamNameParts } from 'pages/provisioning/day2/github-onboarding-new/utils';
import { isValidName } from 'pages/provisioning/day2/github-onboarding-new/utils/formValidation';
import {
    MAX_TEAM_MEMBERS,
    MAX_DEVOPS_TEAM_MEMBERS,
    SERVICE_ACCOUNT_EMAIL,
    TEAM_SUFFIX_DEVELOPERS,
    TEAM_SUFFIX_DEVOPS,
} from 'pages/provisioning/day2/github-onboarding-new/utils/constant';

const useShouldDisableSave = () => {
    const draftTeams = useRecoilValue(recoilStoreDraftTeams) || [];
    const order = useRecoilValue(sspSubmitOrder);

    return useMemo(() => {
        const isReaderRadioUnanswered = !order?.isReaderTeamRequired;
        const hasMissingName = draftTeams?.some(({ name, isNew }) => {
            const customNameParts = getCustomTeamNameParts(name, order.businessApplication);

            return isNew && Object.values(customNameParts).some((part) => !part);
        });

        // Check if any custom team names have invalid characters
        const hasInvalidCustomName = draftTeams?.some(({ name, isNew }) => {
            if (!isNew) return false;

            const customNameParts = getCustomTeamNameParts(name, order.businessApplication);
            const { customName } = customNameParts;

            // If customName exists and is not valid, disable save
            if (customName && !isValidName(customName)) {
                return true;
            }

            return false;
        });

        const nameCounts = draftTeams?.reduce((acc, { name }) => {
            const lower = name.trim().toLowerCase();
            acc[lower] = (acc[lower] || 0) + 1;
            return acc;
        }, {});
        const hasDuplicateNames = Object.values(nameCounts)?.some((count) => count > 1);

        const hasInvalidMembers = draftTeams?.some(({ name = '', members = [], existingOrgMembers = [], hasExistingMembers }) => {
            const totalMembers = members.length + existingOrgMembers.length;
            const lowerName = name.toLowerCase();
            const isDevelopersTeam = lowerName.endsWith(TEAM_SUFFIX_DEVELOPERS);
            const isDevopsTeam = lowerName.endsWith(TEAM_SUFFIX_DEVOPS);

            if (isDevelopersTeam) {
                // Developers team: no limit, but new team requires at least 1 member
                if (!hasExistingMembers) {
                    return !members.length;
                }
                return false;
            }

            // DevOps team: max 5 members (6 if service account is present in existing members)
            if (isDevopsTeam) {
                const hasServiceAccount = existingOrgMembers.some((m) => m.email === SERVICE_ACCOUNT_EMAIL);
                const limit = hasServiceAccount ? MAX_TEAM_MEMBERS : MAX_DEVOPS_TEAM_MEMBERS;
                if (hasExistingMembers) {
                    return totalMembers > limit;
                }
                return !members.length || totalMembers > limit;
            }

            if (hasExistingMembers) {
                return totalMembers > MAX_TEAM_MEMBERS;
            }
            return !members.length || totalMembers > MAX_TEAM_MEMBERS;
        });

        return isReaderRadioUnanswered || hasMissingName || hasInvalidCustomName || hasInvalidMembers || hasDuplicateNames;
    }, [draftTeams, order.businessApplication, order?.isReaderTeamRequired]);
};

export default useShouldDisableSave;
